import React from 'react';
import { X, Calendar } from 'lucide-react';

/**
 * Timeline Event Popup — shows detailed info + "Research Mode" snapshot CTA.
 *
 * Props:
 *   - event: a row from timeline.events
 *   - onClose
 *   - onOpenSnapshot: (dateStr, timeStr) => fires research mode (Module 12/13)
 */

const TYPE_LABELS = {
  sign_change: 'राशि परिवर्तन',
  nakshatra_change: 'नक्षत्र परिवर्तन',
  pada_change: 'पाद परिवर्तन',
  retrograde_start: 'वक्री प्रारंभ',
  retrograde_end: 'मार्गी (वक्री समाप्त)',
  combust_start: 'अस्त प्रारंभ',
  combust_end: 'अस्त समाप्त',
};

export default function TimelineEventPopup({ event, onClose, onOpenSnapshot }) {
  if (!event) return null;
  // Parse "2027-07-15 14:22 IST" → date / time / tz
  const m = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/.exec(event.dt_ist || '');
  const dateStr = m?.[1];
  const timeStr = m?.[2];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="timeline-event-popup">
      <div className="relative w-full max-w-lg rounded-2xl border border-gold/40 bg-gradient-to-br from-purple-950 via-bg-primary to-purple-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="popup-close-btn" className="absolute top-4 right-4 text-text-muted hover:text-gold">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-heading text-2xl text-gold mb-1">{event.planet_hi}</h3>
        <p className="text-xs text-text-muted mb-4">{TYPE_LABELS[event.event_type] || event.event_type}</p>

        <div className="space-y-2 mb-4">
          <Row label="दिनांक / समय (IST)" value={event.dt_ist} mono highlight />
          <Row label="ग्रह" value={`${event.planet_hi} (${event.planet_en})`} />
          <Row label="परिवर्तन" value={`${event.from_label_hi ?? '—'} → ${event.to_label_hi ?? '—'}`} highlight />
          {event.sign_hi && <Row label="राशि (समय पर)" value={event.sign_hi} />}
          {event.duration_days != null && <Row label="अवधि" value={`${event.duration_days} दिन`} mono />}
        </div>

        {dateStr && timeStr && (
          <button
            data-testid="open-snapshot-btn"
            onClick={() => onOpenSnapshot && onOpenSnapshot(dateStr, timeStr)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gold/20 border border-gold/50 text-gold hover:bg-gold/30 transition-all font-semibold"
          >
            <Calendar className="w-4 h-4" />
            इस तिथि का पूरा गोचर देखें (Research Snapshot)
          </button>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, mono, highlight }) => (
  <div className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 ${highlight ? 'bg-gold/10 border border-gold/30' : 'bg-white/5 border border-white/10'}`}>
    <div className="text-[11px] uppercase tracking-wider text-text-muted w-1/2">{label}</div>
    <div className={`text-sm flex-1 text-right ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>{value ?? '—'}</div>
  </div>
);
